import React, { useRef, useEffect, useState, useContext } from 'react';
import styles from './../../styles/form.module.css';
import { useForm } from 'react-hook-form';
import {
  factorEventTracker,
  getRoute,
  gtmEventTracker,
} from './../../helpers/utils';
import { signupOptions, defaultValues } from './options';
import {
  ACCEPT_TERMS,
  AlertConfig,
  AUTH_FLOW,
  SIGNUP_SUCCESS,
  DOMAIN,
  EMAIL,
  FAIL,
  GOOGLE,
  GOOGLE_AUTH2,
  LOGIN,
  ON_CHANGE,
  privacyPolicy,
  SIGNUP,
  SUCCESS,
  termofService,
  USER_EXISTS,
  VERIFICATION_SEND
} from './../../content/constants';
import {
  Info,
  FormHeader,
  Logo,
  SocialAuthProvider,
  InputField,
  Button,
  NavigateLink,
  AlertBox
} from './../../components';
import auth0 from 'auth0-js';
import { useCookies } from 'react-cookie';
import { useTranslation, Trans } from 'react-i18next';
import AuthContext from '../../context/AuthContext';

const Signup = () => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    clearErrors,
    setValue
  } = useForm({
    mode: ON_CHANGE
  });
  const [, setCookie] = useCookies([AUTH_FLOW]);
  const [email, setEmail] = useState('');
  const alertRef = useRef();
  const { data } = useContext(AuthContext);
  const logoUrl = data.tenantDetail?.logo || null;
  const params = data.params;
  var captcha;
  if (params) {
    var webAuth = new auth0.WebAuth(params);
    captcha = webAuth.renderCaptcha(
      document.querySelector('.captcha-container')
    );
  }
  useEffect(() => {
    document.title = t('signup_page_title');
    if (params?.console && params?.email) {
      setValue('email', params?.email);
    }
  }, [params, setValue, t]);

  const signupCallback = (err, res) => {
    setSubmitting(false);
    if (err) {
      factorEventTracker({
        email: res?.email,
        channel: EMAIL,
        formType: SIGNUP,
        formSubmision: FAIL
      });
      switch (err.code || err?.original?.response?.body?.message) {
        // Handle errors based on code
        case USER_EXISTS:
          setError(EMAIL, signupOptions.emailManual);
          break;
        default:
          console.error('Error While signup');
      }
    } else {
      factorEventTracker({
        email: res?.email,
        channel: EMAIL,
        formType: SIGNUP,
        formSubmision: SUCCESS.toLowerCase()
      });
      gtmEventTracker(SIGNUP, { authMethod: EMAIL });
      if (data.isConsole) {
        window.location = `${getRoute(
          SIGNUP,
          LOGIN
        )}&message=${SIGNUP_SUCCESS}`;
        return;
      }
      setEmail(res?.email);
      alertRef.current.showAlert(AlertConfig[VERIFICATION_SEND]);
      window.grecaptcha.reset();
      for (const key in defaultValues) {
        setValue(key, defaultValues[key]);
      }
    }
  };
  
  const onSubmit = async (data) => {
    if(!captcha.getValue()) {
      setCaptchaError(signupOptions.captcha.required);
      return;
    };
    setCaptchaError('');
    setSubmitting(true);
    var auth0Client = new auth0.WebAuth(params);
    auth0Client.signup(
      {
        connection: params?.connection,
        password: data?.password,
        email: data?.email,
        captcha: captcha.getValue(),
        given_name: data.firstName,
        family_name: data.lastName,
        name: `${data.firstName} ${data.lastName}`,
        user_metadata: userMetaData()
      },
      signupCallback
    );
  };
  const userMetaData = () => ({
    ...(params?.referral && { referral: params.referral }),
    ...(params?.invite_token && { invite_token: params.invite_token })
  });

  const providerHandler = (provider) => {
    setCookie(AUTH_FLOW, `${SIGNUP}-${provider}`, {
      path: '/',
      domain: DOMAIN
    });
    var auth0Client = new auth0.WebAuth(params);
    const source = provider === GOOGLE ? GOOGLE_AUTH2 : provider;
    auth0Client.authorize(
      {
        connection: source
      },
      function (err) {
        console.error('Error While Authenticating with provider', err);
      }
    );
  };
  const termsConditionHandler = (value) => {
    if (!value) {
      setError(ACCEPT_TERMS, signupOptions.acceptTermsManual);
      return;
    }
    setValue(ACCEPT_TERMS, value);
    clearErrors(ACCEPT_TERMS);
  };
  return (
    <div className={`${styles.auth_wrapper} ${
      logoUrl ? styles.auth_align_center : ''
    }`}>
      <div className={styles.logo}>
        <Logo logoUrl={logoUrl} />
      </div>
      {!logoUrl ? <Info logoUrl={logoUrl} /> : <div></div>}
      <div className={styles.form_container}>
        <AlertBox ref={alertRef} email={email} />
        <form onSubmit={handleSubmit(onSubmit)} className={styles.w_90}>
          <div>
            <FormHeader heading={t('get_started')} />
            <NavigateLink
              text={t('already_have_an_account')}
              linkText={t('login')}
              linkUrl={getRoute(SIGNUP, LOGIN)}
              data-testid="signup-login-link"
            />
            <div className={styles.col_separator}>
              <div className={styles.right_space}>
                <InputField
                  label={t('first_name')}
                  placeholder={t('enter_first_name')}
                  type="text"
                  customClassName={errors?.firstName?.message ? 'error' : ''}
                  register={register('firstName', signupOptions.firstName)}
                  data-testid="signup-first-name-input-field"
                />
                <span className={styles.err_text}>
                  {t(errors?.firstName?.message)}
                </span>
              </div>
              <div>
                <InputField
                  label={t('last_name')}
                  placeholder={t('enter_last_name')}
                  type="text"
                  customClassName={errors?.lastName?.message ? 'error' : ''}
                  register={register('lastName', signupOptions.lastName)}
                  data-testid="signup-last-name-input-field"
                />
                <span className={styles.err_text}>
                  {t(errors?.lastName?.message)}
                </span>
              </div>
            </div>
            <InputField
              label={t('email')}
              placeholder={t('enter_email_address')}
              type="email"
              name="email"
              id="email"
              disabled={params?.console && params?.email}
              customClassName={errors?.email?.message ? 'error' : undefined}
              register={register('email', signupOptions.email)}
              data-testid="signup-email-address-input-field"
            />
            <span className={styles.err_text}>{t(errors?.email?.message)}</span>
            <InputField
              label={t('password')}
              placeholder={t('enter_password')}
              type="password"
              id="password"
              customClassName={errors?.password?.message ? 'error' : ''}
              name="password"
              register={register('password', signupOptions.password)}
              data-testid="signup-password-input-field"
            />
            <span className={styles.err_text}>
              {t(errors?.password?.message)}
            </span>

            <div className={styles.privacy_policy_container}>
              <input
                className={styles.checkbox}
                type="checkbox"
                {...register('acceptTerms', signupOptions.acceptTerms)}
                onChange={(e) => termsConditionHandler(e.target.checked)}
                data-testid="signup-accept-terms-checkbox"
              />
              <Trans i18nKey="accept_terms_privacy">
                <span className={styles.checkbox}>
                  I agree to all the
                  <a
                    target="_blank"
                    href={termofService}
                    className={styles.link}
                    rel="noreferrer"
                    data-testid="signup-terms-of-service-link">
                    Terms of Service
                  </a>
                  and
                  <a
                    target="_blank"
                    href={privacyPolicy}
                    className={styles.link}
                    rel="noreferrer"
                    data-testid="signup-privacy-policy-link">
                    Privacy Policy
                  </a>
                </span>
              </Trans>
            </div>
            <span className={styles.err_text}>
              {t(errors?.acceptTerms?.message)}
            </span>

            <div className="captcha-container"></div>
            {!!captchaError && (
              <span className={styles.err_text}>
                {t(captchaError)}
              </span>
            )}

            <div>
              <Button
                showSpinner={isSubmitting}
                label={t('signup')}
                type="submit"
                testId="submit-btn"
                data-testid="signup-submit-button"
              />
            </div>

            <div className={styles.auth_separator}>
              <p className={styles.separator_line}>{t('or')}</p>
            </div>
            <SocialAuthProvider
              label={t('sign_up_with')}
              isConsole={data.isConsole}
              onClickProvider={providerHandler}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
